import { useEffect, useState } from 'react';

export interface VisualViewportRect {
  /** Height of the area actually visible to the user, in px. */
  height: number;
  /** How far the visual viewport is scrolled down inside the layout viewport. */
  offsetTop: number;
  /** True when the visible area is materially shorter than the layout
   *  viewport — i.e. an on-screen keyboard (or similar) is up. */
  keyboardOpen: boolean;
}

/**
 * Tracks `window.visualViewport`.
 *
 * Why this is needed at all: on iOS the on-screen keyboard does NOT
 * shrink the *layout* viewport. `100vh`, `top: 50%` and `position:
 * fixed` all keep measuring the full screen, so a vertically-centred
 * fixed dialog keeps its centre behind the keyboard and the fields the
 * user is typing into become unreachable. Only the visual viewport
 * reflects reality, and only via this API.
 *
 * Falls back to `window.innerHeight` where the API is missing (older
 * Firefox), which degrades to exactly the previous behaviour.
 */
export function useVisualViewport(): VisualViewportRect {
  const read = (): VisualViewportRect => {
    if (typeof window === 'undefined') {
      return { height: 0, offsetTop: 0, keyboardOpen: false };
    }
    const vv = window.visualViewport;
    const layoutHeight = window.innerHeight;
    if (!vv) {
      return { height: layoutHeight, offsetTop: 0, keyboardOpen: false };
    }
    return {
      height: vv.height,
      offsetTop: vv.offsetTop,
      // 150px of slack: mobile browsers shave a few dozen px for
      // collapsing toolbars, which must not read as a keyboard.
      keyboardOpen: layoutHeight - vv.height > 150,
    };
  };

  const [rect, setRect] = useState<VisualViewportRect>(read);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setRect(read());
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return rect;
}
